import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Stack,
  TextField,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import { FloatingLabelSelect } from '../../../../../../ui';
import { CustomDatePicker } from '../../../../../../ui/components/Common/TextFields/CustomDatePicker';
import { CustomTimePicker } from '../../../../../../ui/components/Common/TextFields/CustomTimePicker';
import { useConfigurations } from './Hooks';

interface IConfiguration {
  interviewId: string;
  onRoundCreated: () => void;
  jobOpeningId: string;
}

export const Configurations: React.FC<IConfiguration> = ({
  interviewId,
  onRoundCreated,
  jobOpeningId,
}) => {
  const { t } = useTranslation(NS.adminPages);
  const theme = useTheme();
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;

  const {
    title,
    setTitle,
    defaultEmployeeUserOptions,
    handleHostChange,
    loadOptionsDebounced,
    handleDateSelection,
    handleTimeSlotSelection,
    handlePanelists,
    selectedDates,
    selectedTimes,
    host,
    panelists,
    interviewData,
    resetForm,
    isChanged,
    handleInputChange,
    isFormValid,
    errors,
    disabledSlots,
    buttonHandler,
    touched,
    handleBlur,
  } = useConfigurations({
    interviewId,
    setInterviewId: () => {},
    onRoundCreated,
  });

  return (
    <Box sx={{ width: '100%', py: 1 }}>
      {/* 2-Column Responsive Layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', lg: '1fr 1.15fr' },
          gap: 3.5,
          alignItems: 'start',
          width: '100%',
        }}
      >
        {/* Left Column: Form Details & Calendar */}
        <Stack spacing={2.5}>
          {/* Round Title */}
          <Box>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: 'text.secondary',
                fontSize: '0.6875rem',
                mb: 0.75,
              }}
            >
              {t(SI.configurations.roundTitle)}
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={title}
              placeholder={t(SI.configurations.roundTitlePlaceholder)}
              onChange={(e) => {
                setTitle(e.target.value);
                handleInputChange('title');
              }}
              onBlur={() => handleBlur('title')}
              error={Boolean(touched.title && errors.title)}
              helperText={
                touched.title && errors.title ? t(SI.configurations.errors.title) : undefined
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: 'background.paper',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Box>

          {/* Host Select */}
          <Box>
            <FloatingLabelSelect
              label={t(SI.configurations.host)}
              required
              isMulti={false}
              value={host}
              onChange={(v) => {
                handleHostChange(v!);
                handleInputChange('host');
              }}
              loadOptions={loadOptionsDebounced}
              defaultOptions={defaultEmployeeUserOptions}
              error={touched.host && errors.host}
              helperText={touched.host && errors.host ? t(SI.configurations.errors.host) : ''}
              onBlur={() => handleBlur('host')}
            />
          </Box>

          {/* Panelists Select */}
          <Box>
            <FloatingLabelSelect
              label={t(SI.configurations.panelists)}
              required
              isMulti
              value={panelists}
              onChange={(v) => {
                handlePanelists(v as any[]);
                handleInputChange('panelists');
              }}
              loadOptions={loadOptionsDebounced}
              defaultOptions={defaultEmployeeUserOptions}
              error={touched.panelists && errors.panelists}
              helperText={
                touched.panelists && errors.panelists
                  ? t(SI.configurations.errors.panelists)
                  : ''
              }
              onBlur={() => handleBlur('panelists')}
            />
          </Box>

          {/* Custom Date Picker */}
          <Box>
            <CustomDatePicker
              onSelectDates={(d) => {
                handleDateSelection(d);
                handleInputChange('selectedDates');
              }}
              selectedDates={selectedDates}
              error={Boolean(touched.selectedDates && errors.selectedDates)}
              helperText={
                touched.selectedDates && errors.selectedDates
                  ? 'Please select at least one date.'
                  : undefined
              }
              onBlur={() => handleBlur('selectedDates')}
            />
          </Box>
        </Stack>

        {/* Right Column: Time Slots Selection */}
        <Box>
          <CustomTimePicker
            selectedDates={selectedDates}
            selectedTimes={selectedTimes}
            onTimeSelect={(d, t) => {
              handleTimeSlotSelection(d, t);
              handleInputChange('selectedTimes');
            }}
            error={Boolean(touched.selectedTimes && errors.selectedTimes)}
            helperText={
              touched.selectedTimes && errors.selectedTimes
                ? 'Please select at least one time slot.'
                : ''
            }
            disabledSlots={disabledSlots}
          />
        </Box>
      </Box>

      {/* Bottom Actions Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 2,
          mt: 4,
          pt: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button
          variant="outlined"
          onClick={resetForm}
          sx={{
            borderRadius: '9999px',
            px: 3.5,
            py: 0.85,
            fontSize: '0.8125rem',
            fontWeight: 600,
            textTransform: 'none',
            color: 'text.secondary',
            borderColor: 'divider',
            '&:hover': {
              bgcolor: alpha(theme.palette.action.hover, 0.06),
              borderColor: 'text.secondary',
            },
          }}
        >
          {t(SI.configurations.cancel)}
        </Button>

        <Button
          variant="contained"
          onClick={buttonHandler}
          disabled={!isChanged || !isFormValid}
          sx={{
            borderRadius: '9999px',
            px: 4,
            py: 0.85,
            fontSize: '0.8125rem',
            fontWeight: 600,
            textTransform: 'none',
            bgcolor: 'primary.main',
            color: '#ffffff',
            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            '&:hover': {
              bgcolor: 'primary.dark',
              boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.45)}`,
            },
            '&.Mui-disabled': {
              bgcolor: alpha(theme.palette.action.disabledBackground, 0.8),
              color: 'text.disabled',
              boxShadow: 'none',
            },
          }}
        >
          {interviewData ? t(SI.configurations.updateRound) : t(SI.configurations.createRound)}
        </Button>
      </Box>
    </Box>
  );
};

